<template>
  <div v-if="ochinView">
    <generic-loader caption="Fetching pack data..." topMargin="2" v-if="packDataIsLoading"></generic-loader>
    <v-data-table
      :headers="ochinHeaders"
      :hide-default-footer="true"
      :items="packList"
      data-cy="ochinActivePackList"
      data-test="ochinActivePack"
      disable-pagination
      elevation="0"
      item-key="id"
      must-sort
      v-else
    >
      <template v-slot:item="{ item }">
        <tr
          class="widgetActivePackList__item"
          @click="navigateToDashboard(item.id)"
          data-cy="widgetActivePackListOchin__item"
        >
          <td data-cy="widgetActivePackListOchin-type">{{ item.type }}</td>
          <td data-cy="widgetActivePackListOchin-title">
            <span class="font-weight-bold">{{ item.title }}</span>
          </td>
          <td data-cy="widgetActivePackListOchin-timeline">
            <div class="font-italic" data-cy="widgetActivePackListOchin-uat">
              UAT: {{ formatUatDateRange(item.uatStart, item.uatEnd) }}
            </div>
            <div class="font-italic" data-cy="widgetActivePackListOchin-productionDate" v-if="item.productionDate">
              PRD: {{ formatProductionDate(item.productionDate) }}
            </div>
          </td>
          <td data-cy="widgetActivePackListOchin-packStatus">{{ item.status }}</td>
          <td data-cy="widgetActivePackListOchin-releaseNoteTotal">{{ item.releaseNoteTotalPublished }}</td>
        </tr>
      </template>
    </v-data-table>
  </div>
  <div v-else>
    <generic-loader caption="Fetching pack data..." topMargin="2" v-if="packDataIsLoading"></generic-loader>
    <v-data-table
      :headers="memberHeaders"
      :hide-default-footer="true"
      :items="formattedPackList"
      data-cy="memberActivePackList"
      data-test="memberActivePack"
      disable-pagination
      elevation="0"
      item-key="id"
      must-sort
      v-else
    >
      <template v-slot:item="{ item }">
        <tr
          class="widgetActivePackList__item"
          @click="navigateToDashboard(item.id)"
          data-cy="widgetActivePackList__item"
        >
          <td data-cy="widgetActivePackList__item--packType">{{ item.formattedPackType }}</td>
          <td data-cy="widgetActivePackList__item--title">
            <span class="font-weight-bold">{{ item.title }}</span>
          </td>
          <td data-cy="widgetActivePackList__item--memberPackLead">
            <div>{{ item.memberPackLeadDisplayName || "-" }}</div>
            <div v-if="item.memberPackLeadDisplayName" class="text-caption grey--text text--darken-2">
              {{ item.memberPackLeadEmail }}
            </div>
          </td>
          <td data-cy="widgetActivePackList__item--timeline">
            <div class="font-italic" data-cy="widgetActivePackList__item--uat">
              UAT: {{ item.formattedUatDateRange }}
            </div>
            <div class="font-italic" data-cy="widgetActivePackList__item--productionDate" v-if="item.productionDate">
              PRD: {{ item.formattedProductionDate }}
            </div>
          </td>
          <td data-cy="widgetActivePackList__item--packStatus">{{ item.status }}</td>
          <td data-cy="widgetActivePackList__item--releaseNoteTotal">{{ item.releaseNoteTotalPublished }}</td>
          <td data-cy="widgetActivePackList__item--unassigned">{{ item.unassigned }}</td>
          <td data-cy="widgetActivePackList__item--failedQuestion">{{ item.failedQuestion }}</td>
          <td data-cy="widgetActivePackList__item--passedReviewed">{{ item.passedReviewed }}</td>
          <td data-cy="widgetActivePackList__item--uatProgression">{{ item.uatStatus }}</td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import moment from "moment";
import uatUtil from "@/lib/uatUtil";
import GenericLoader from "@/components/GenericLoader";
import { mapGetters, mapState } from "vuex";
import { getDashboardRedirect } from "@/router/dashboardRedirect";

export default {
  name: "activePacksAtAGlance",
  components: { GenericLoader },
  data: () => ({
    memberHeaders: [
      {
        text: "Pack Type",
        value: "type",
      },
      {
        text: "Pack Title",
        value: "title",
      },
      {
        text: "Pack Lead",
        value: "memberPackLeadDisplayName",
      },
      {
        text: "Timeline",
        value: "uatStart",
      },
      {
        text: "Pack Status",
        value: "status",
      },
      {
        text: "# RNs",
        value: "releaseNoteTotalPublished",
      },
      {
        text: "# Unassigned",
        value: "unassigned",
      },
      {
        text: "# Failed/Question",
        value: "failedQuestion",
      },
      {
        text: "# Passed/Reviewed",
        value: "passedReviewed",
      },
      {
        text: "UAT Progression",
        value: "uatStatus",
      },
    ],
    ochinHeaders: [
      {
        text: "Pack Type",
        value: "type",
      },
      {
        text: "Pack Title",
        value: "title",
      },
      {
        text: "Timeline",
        value: "uatStart",
      },
      {
        text: "Pack Status",
        value: "status",
      },
      {
        text: "# RNs",
        value: "releaseNoteTotalPublished",
      },
    ],
  }),
  computed: {
    ...mapGetters({
      hasDashboardQaView: "session/hasDashboardQaView",
      hasDashboardAccountManagerView: "session/hasDashboardAccountManagerView",
      hasDashboardUatView: "session/hasDashboardUatView",
    }),
    ...mapState({
      serviceArea: state => state.session.user.serviceArea,
    }),
    formattedPackList() {
      return this.packList.map(pack => {
        const hasRns = !!pack.releaseNoteTotalPublished;
        const statusDefaults = { failed: 0, question: 0, passed: 0, reviewed: 0 };
        const statusCount = Object.assign({}, statusDefaults, pack.statusCount);
        return Object.assign({}, pack, {
          formattedPackType: this.formatPackType(pack.type),
          formattedUatDateRange: this.formatUatDateRange(pack.uatStart, pack.uatEnd),
          formattedProductionDate: this.formatProductionDate(pack.productionDate),
          unassigned: hasRns ? (pack.assignedToCount && pack.assignedToCount.unassigned) || 0 : 0,
          failedQuestion: hasRns ? statusCount.failed + statusCount.question : 0,
          passedReviewed: hasRns ? statusCount.passed + statusCount.reviewed : 0,
          uatStatus: hasRns ? uatUtil.getStatusLabel(pack) : "-",
        });
      });
    },
  },
  methods: {
    navigateToDashboard(packId) {
      // Determine the correct dashboard route based on user permissions
      // This avoids navigating to the generic dashboard route which would
      // trigger a redirect and cause a promise rejection
      const permissions = {
        hasDashboardQaView: this.hasDashboardQaView,
        hasDashboardAccountManagerView: this.hasDashboardAccountManagerView,
        hasDashboardUatView: this.hasDashboardUatView,
        serviceArea: this.serviceArea,
      };

      const redirect = getDashboardRedirect(permissions, {
        // Passing `name: null` indicates that navigation is being triggered from outside any dashboard route,
        // or that no current dashboard route name should be used for comparison in the redirect logic.
        name: null,
        packId,
      });

      // Navigate to the specific dashboard type, or default to generic dashboard
      this.$router.push(redirect || { name: "dashboard", params: { packId } });
    },
    formatUatDateRange(uatStart, uatEnd) {
      if (!uatStart && !uatEnd) {
        return "TBA";
      }

      const formattedUatStart = uatStart ? moment(uatStart).format("MM/DD/YYYY") : "TBA";
      const formattedUatEnd = uatEnd ? moment(uatEnd).format("MM/DD/YYYY") : "TBA";

      return `${formattedUatStart} - ${formattedUatEnd}`;
    },
    formatPackType(packType) {
      if (!packType) {
        return "-";
      }

      if (packType === "Enhancement Pack" || packType === "Service Pack" || packType === "Fix Pack") {
        return packType;
      }
      return `${packType} Pack`;
    },
    formatProductionDate(productionDate) {
      if (!productionDate) {
        return "TBA";
      }
      return moment(productionDate).format("MM/DD/YYYY");
    },
  },
  props: {
    packList: { required: true, type: Array },
    packDataIsLoading: { default: false, required: true, type: Boolean },
    ochinView: { default: false, type: Boolean },
  },
};
</script>
